<template>
  <div :class="$style.wrapper">
    <!-- Скелетон загрузки -->
    <template v-if="isLoading">
      <div :class="$style.skeletonForm">
        <!-- Скелетон названия модели -->
        <div :class="$style.skeletonFieldGroup">
          <div :class="['skeleton', $style.skeletonLabel]"></div>
          <div :class="['skeleton', $style.skeletonInput]"></div>
        </div>

        <!-- Скелетон галереи -->
        <div :class="['skeleton', $style.skeletonGallery]"></div>

        <!-- Скелетон варианта -->
        <div :class="$style.skeletonVariantCard">
          <div :class="$style.skeletonVariantTopRow">
            <div :class="['skeleton', $style.skeletonSize]"></div>
            <div :class="['skeleton', $style.skeletonCount]"></div>
            <div :class="['skeleton', $style.skeletonPrice]"></div>
          </div>
          <div :class="['skeleton', $style.skeletonDivider]"></div>
          <div :class="$style.skeletonVariantBottomRow">
            <div :class="['skeleton', $style.skeletonToggle]"></div>
            <div :class="['skeleton', $style.skeletonToggle]"></div>
            <div :class="['skeleton', $style.skeletonToggle]"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Основная форма -->
    <form v-else :class="$style.form" @submit.prevent="handleSubmit">
      <!-- Название модели -->
      <div :class="$style.fieldGroup">
        <div :class="$style.sectionLabel">Название модели или артикул</div>
        <div :class="[$style.inputCard, $style.inputCardDisabled]">
          <input
            :value="form.modelTitle"
            type="text"
            placeholder="Выберите модель"
            readonly
            disabled
            :class="$style.modelInput"
          />
        </div>
      </div>

      <!-- Галерея изображений модели -->
      <div
        v-if="modelImages && modelImages.length > 0"
        :class="$style.galleryWrap"
      >
        <ProductGallerySimple
          :images="modelImages"
          :alt-base="form.modelTitle || 'Фото модели'"
        />
      </div>

      <!-- Список вариантов (карточки) -->
      <section :class="$style.variantsSection">
        <div
          v-for="variant in variants"
          :key="variant.id"
          :class="$style.variantCard"
        >
          <div :class="$style.variantTopRow">
            <!-- Размер -->
            <div :class="$style.variantInputBlock">
              <div :class="[$style.combinedInput, $style.sizeCombined]">
                <div :class="$style.sizeInputWrapper">
                  <input
                    ref="sizeInputs"
                    type="text"
                    v-model.trim="variant.size"
                    :placeholder="getSizePlaceholder(variant.size_type)"
                    :class="[$style.invisibleInput, $style.sizeInput]"
                    :style="{
                      width: variant.size
                        ? `${String(variant.size).length}ch`
                        : `${getSizePlaceholder(variant.size_type).length}ch`,
                    }"
                    required
                    @blur="normalizeSize(variant)"
                    @input="hideSizeAlert(variant)"
                  />
                  <div v-if="variant.showSizeAlert" :class="$style.sizeAlert">
                    {{ getSizeAlertMessage(variant.size_type) }}
                  </div>
                </div>
                <div :class="$style.sizeTypeWrapper">
                  <span
                    v-if="variant.size_type"
                    :class="$style.sizeTypeLabel"
                    @click="openSizeTypeDropdown(variant.id, $event)"
                  >
                    {{ getSizeTypeLabel(variant.size_type) }}
                    <svg
                      :class="[
                        $style.sizeTypeArrow,
                        openDropdownId === variant.id &&
                          $style.sizeTypeArrowOpen,
                      ]"
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  <Teleport to="body">
                    <div
                      v-if="
                        openDropdownId === variant.id ||
                        dropdownClosingId === variant.id
                      "
                      :class="[
                        $style.sizeTypeDropdown,
                        dropdownClosingId === variant.id &&
                          $style.sizeTypeDropdownClosing,
                      ]"
                      :style="dropdownStyle"
                      @click.stop
                    >
                      <button
                        v-for="type in sizeTypes"
                        :key="type"
                        type="button"
                        :class="[
                          $style.sizeTypeOption,
                          variant.size_type === type &&
                            $style.sizeTypeOptionActive,
                        ]"
                        @click="selectSizeType(variant.id, type)"
                      >
                        {{ getSizeTypeLabel(type) }}
                      </button>
                    </div>
                  </Teleport>
                </div>
              </div>
            </div>

            <!-- Кол-во -->
            <div :class="$style.variantInputBlock">
              <div :class="[$style.combinedInput, $style.countCombined]">
                <input
                  ref="countInputs"
                  v-model="variant.count"
                  inputmode="numeric"
                  placeholder="20"
                  :class="[$style.invisibleInput, $style.countInput]"
                  required
                />
                <span>пар</span>
              </div>
            </div>

            <!-- Стоимость -->
            <div :class="[$style.variantInputBlock, $style.priceBlock]">
              <div :class="[$style.combinedInput, $style.priceCombined]">
                <input
                  ref="priceInputs"
                  v-model="variant.price"
                  inputmode="numeric"
                  placeholder="185 000"
                  :class="[$style.invisibleInput, $style.priceInput]"
                  required
                />
                <span>₽</span>
              </div>
            </div>
          </div>

          <div :class="$style.variantDivider"></div>

          <div :class="$style.variantBottomRow">
            <div :class="$style.variantBottomItem">
              <span>Возможность торга</span>
              <label :class="$style.toggle">
                <input type="checkbox" v-model="variant.allowTrade" />
                <span></span>
              </label>
            </div>
            <div :class="$style.variantBottomItem">
              <span>Кастомные фото</span>
              <label :class="$style.toggle">
                <input type="checkbox" v-model="variant.isCustom" />
                <span></span>
              </label>
            </div>
            <div :class="$style.variantBottomItem">
              <span>Наличие коробки</span>
              <label :class="[$style.toggle, $style.toggleBlack]">
                <input type="checkbox" v-model="variant.hasBox" />
                <span></span>
              </label>
            </div>
          </div>

          <!-- Загрузка кастомных изображений -->
          <div v-if="variant.isCustom" :class="$style.customImagesSection">
            <div :class="$style.customImagesLabel">
              Фотографии кастомных кроссовок
            </div>
            <p :class="$style.customImagesRequirements">
              Кроссовки должны быть сняты на белом фоне. Форматы: JPG, PNG или WebP. Размер: от 800×800 до 4096×4096 px, не более 5 МБ на файл.
            </p>
            <div :class="$style.customImagesGrid">
              <!-- Первое изображение -->
              <div :class="$style.customImageWrapper">
                <input
                  :ref="`customImage1_${variant.id}`"
                  type="file"
                  accept="image/*"
                  :class="$style.hiddenInput"
                  @change="handleCustomImageChange(variant, 1, $event)"
                />
                <div
                  v-if="variant.customImage1"
                  :class="$style.customImagePreview"
                >
                  <img
                    :src="getImagePreview(variant.customImage1)"
                    alt="Кастомное фото 1"
                  />
                  <button
                    type="button"
                    :class="$style.removeImageBtn"
                    @click="removeCustomImage(variant, 1)"
                  >
                    <img
                      src="@/assets/img/account/my-products/delete.svg"
                      alt="Удалить"
                    />
                  </button>
                </div>
                <button
                  v-else
                  type="button"
                  :class="$style.addImageBtn"
                  @click="triggerImageInput(variant, 1)"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <!-- Второе изображение -->
              <div :class="$style.customImageWrapper">
                <input
                  :ref="`customImage2_${variant.id}`"
                  type="file"
                  accept="image/*"
                  :class="$style.hiddenInput"
                  @change="handleCustomImageChange(variant, 2, $event)"
                />
                <div
                  v-if="variant.customImage2"
                  :class="$style.customImagePreview"
                >
                  <img
                    :src="getImagePreview(variant.customImage2)"
                    alt="Кастомное фото 2"
                  />
                  <button
                    type="button"
                    :class="$style.removeImageBtn"
                    @click="removeCustomImage(variant, 2)"
                  >
                    <img
                      src="@/assets/img/account/my-products/delete.svg"
                      alt="Удалить"
                    />
                  </button>
                </div>
                <button
                  v-else
                  type="button"
                  :class="$style.addImageBtn"
                  @click="triggerImageInput(variant, 2)"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div :class="$style.links">
        <button type="button" @click="openFaq">
          Перейти к FAQ
          <span :class="$style.arrowIcon">›</span>
        </button>
      </div>

      <div :class="$style.submitBar">
        <button
          v-if="!hasTelegramMainButton"
          type="submit"
          :disabled="!canSubmit || isSubmitting"
          :class="$style.submitBtn"
        >
          {{ isSubmitting ? "Сохраняем..." : "Сохранить изменения" }}
        </button>
        <button
          type="button"
          :class="$style.deleteProductBtn"
          :disabled="isSubmitting"
          @click="handleDeleteProduct"
        >
          Удалить товар
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import ProductGallerySimple from "@/components/ProductGallerySimple.vue"
import {
  getSizeLimits,
  isValidSize,
  convertSize,
  findClosestSize,
  SIZE_CHART,
} from "@/utils/sizeChart.js"

export default {
  name: "EditProduct",
  components: { ProductGallerySimple },
  data() {
    return {
      productId: null,
      moderationStatus: null,
      form: {
        modelTitle: "",
        modelId: null,
      },
      variants: [],
      isSubmitting: false,
      isLoading: true,
      hasTelegramMainButton: false,
      mainButtonInstance: null,
      mbHandler: null,
      openDropdownId: null,
      dropdownClosingId: null,
      dropdownPosition: { top: 0, left: 0 },
      sizeTypes: ["EU", "RU", "US", "UK", "CM"],
      modelImagesData: [],
    }
  },
  computed: {
    canSubmit() {
      const hasModelTitle = Boolean(this.form.modelTitle?.trim())
      const modelId = Number(this.form.modelId)
      const hasModelId = !isNaN(modelId) && modelId > 0
      const hasFilledVariant = this.variants.some((variant) =>
        this.isVariantValid(variant)
      )

      // Проверяем кастомные изображения для всех вариантов
      const allVariantsHaveCustomImages = this.variants.every((variant) => {
        // Если тоггл "Кастомные фото" включен, должны быть оба изображения
        if (variant.isCustom) {
          return Boolean(variant.customImage1 && variant.customImage2)
        }
        // Если тоггл выключен, проверка не требуется
        return true
      })

      return (
        hasModelTitle &&
        hasModelId &&
        hasFilledVariant &&
        allVariantsHaveCustomImages
      )
    },
    dropdownStyle() {
      return {
        top: `${this.dropdownPosition.top}px`,
        left: `${this.dropdownPosition.left}px`,
      }
    },
    modelImages() {
      if (!this.modelImagesData || !Array.isArray(this.modelImagesData)) {
        return []
      }
      return this.modelImagesData
    },
  },
  watch: {
    canSubmit() {
      this.updateMainButtonState()
    },
    isSubmitting() {
      this.updateMainButtonState()
    },
    variants: {
      handler() {
        this.updateMainButtonState()
      },
      deep: true,
    },
  },
  async mounted() {
    this.productId = this.$route.params.id
    this.moderationStatus = this.$route.query.moderation_status || null
    await this.loadProduct()
    this.setupMainButton()
    this.updateMainButtonState()
    document.addEventListener("click", this.handleDocumentClick)
    window.addEventListener("scroll", this.handleScroll, true)
  },
  beforeUnmount() {
    this.destroyMainButton()
    document.removeEventListener("click", this.handleDocumentClick)
    window.removeEventListener("scroll", this.handleScroll, true)
    // Очищаем URL preview для кастомных изображений
    this.variants.forEach((variant) => {
      if (variant.customImage1 && typeof variant.customImage1 !== "string") {
        URL.revokeObjectURL(this.getImagePreview(variant.customImage1))
      }
      if (variant.customImage2 && typeof variant.customImage2 !== "string") {
        URL.revokeObjectURL(this.getImagePreview(variant.customImage2))
      }
    })
  },
  methods: {
    getSizeTypeLabel(type) {
      const t = String(type || "").toUpperCase()
      return t
    },
    async loadProduct() {
      if (!this.productId) return
      this.isLoading = true
      try {
        let response
        let data
        let apiProduct
        let apiModel

        // Если продукт на модерации, используем другой запрос
        if (this.moderationStatus === "pending") {
          response = await this.$store.dispatch(
            "getUserProduct",
            this.productId
          )
          data = response || {}
          // В этом случае данные находятся в корне объекта
          apiProduct = data || {}
          apiModel = data.model || {}
        } else {
          // Обычный запрос для опубликованных продуктов
          response = await this.$store.dispatch("getProduct", {
            id: this.productId,
          })
          data = response?.data || response
          apiProduct = data.product || {}
          apiModel = data.model || {}
        }

        // Заполняем форму данными продукта
        this.form.modelTitle = apiModel.title || ""
        this.form.modelId =
          apiModel.id || apiModel.model_id || data.model_id || null

        // Сохраняем изображения модели
        if (Array.isArray(apiModel.images)) {
          this.modelImagesData = apiModel.images
        } else if (apiModel.images) {
          this.modelImagesData = [apiModel.images]
        } else {
          this.modelImagesData = []
        }

        // Создаем вариант из данных продукта
        // Размер приходит в EU, но мы можем отображать его в другом типе
        // По умолчанию оставляем EU
        const euSize = String(apiProduct.size || "")
        const defaultSizeType = (apiProduct.size_type || "EU").toUpperCase()

        // Обрабатываем кастомные изображения из API
        const customImages = apiProduct.custom_images || []
        let customImage1 = null
        let customImage2 = null

        // Если есть кастомные изображения, извлекаем URL из объектов
        if (Array.isArray(customImages) && customImages.length > 0) {
          // Извлекаем URL из первого объекта, если он есть
          if (customImages[0]) {
            customImage1 =
              typeof customImages[0] === "string"
                ? customImages[0]
                : customImages[0]?.url || null
          }
          // Извлекаем URL из второго объекта, если он есть
          if (customImages[1]) {
            customImage2 =
              typeof customImages[1] === "string"
                ? customImages[1]
                : customImages[1]?.url || null
          }
        }

        this.variants = [
          {
            id: Date.now(),
            // Размер приходит в EU, сохраняем как есть для отображения
            size: euSize,
            size_type: defaultSizeType,
            count: String(apiProduct.count || ""),
            price: apiProduct.price
              ? String(Number(apiProduct.price).toLocaleString("ru-RU"))
              : "",
            allowTrade: apiProduct.is_trade || false,
            isCustom: apiProduct.is_custom || false,
            hasBox: apiProduct.has_box || false,
            showSizeAlert: false,
            customImage1: customImage1,
            customImage2: customImage2,
          },
        ]
      } catch (error) {
        console.error("Ошибка при загрузке продукта:", error)
        const tg = window.Telegram?.WebApp
        if (tg?.showPopup) {
          tg.showPopup({
            title: "Ошибка",
            message: "Не удалось загрузить данные продукта",
            buttons: [{ type: "close", text: "Ок" }],
          })
        } else {
          window.alert("Не удалось загрузить данные продукта")
        }
        this.$router.push({ name: "MyProducts" })
      } finally {
        this.isLoading = false
      }
    },
    openModelSelect() {
      // В режиме редактирования модель нельзя менять
    },
    normalizeSize(variant) {
      // Если поле пустое, не валидируем
      if (!variant.size || variant.size.trim() === "") {
        this.hideSizeAlert(variant)
        return
      }

      const sizeType = variant.size_type || "EU"
      const limits = getSizeLimits(sizeType)

      if (limits.min === null || limits.max === null) {
        // Если не удалось определить лимиты, не валидируем
        this.hideSizeAlert(variant)
        return
      }

      // Проверяем, валиден ли размер для текущего типа
      if (isValidSize(variant.size, sizeType)) {
        this.hideSizeAlert(variant)
        return
      }

      // Если размер не валиден, находим ближайший корректный размер из сетки
      const closestSize = findClosestSize(variant.size, sizeType)

      if (closestSize !== null) {
        // Округляем до ближайшего корректного размера
        const oldSize = variant.size
        variant.size = String(closestSize)

        // Показываем модалку о том, что размер был округлен
        if (String(oldSize) !== String(closestSize)) {
          const tg = window.Telegram?.WebApp
          if (tg?.showPopup) {
            tg.showPopup({
              title: "Размер округлен",
              message: `Размер "${oldSize}" был округлен до ближайшего корректного значения "${closestSize}"`,
              buttons: [{ type: "close", text: "Ок" }],
            })
          } else {
            window.alert(
              `Размер "${oldSize}" был округлен до ближайшего корректного значения "${closestSize}"`
            )
          }
        }

        this.hideSizeAlert(variant)
      } else {
        // Если не удалось найти ближайший размер, показываем ошибку
        this.showSizeAlert(variant)
      }
    },
    getSizePlaceholder(sizeType) {
      const type = (sizeType || "EU").toUpperCase()
      const limits = getSizeLimits(type)

      if (limits.min === null || limits.max === null) {
        return "12"
      }

      // Для числовых типов возвращаем пример среднего значения
      if (type === "EU" || type === "RU" || type === "CM") {
        const midValue = (limits.min + limits.max) / 2
        const closest = findClosestSize(midValue, type)
        return closest !== null ? String(closest) : String(midValue)
      }

      // Для строковых типов возвращаем первый валидный размер
      const key = type === "UK" ? "uk" : "us"
      const firstEntry = SIZE_CHART.find((entry) => entry[key] != null)
      return firstEntry ? String(firstEntry[key]) : "12"
    },
    getSizeAlertMessage(sizeType) {
      const type = (sizeType || "EU").toUpperCase()
      const limits = getSizeLimits(type)

      if (limits.min === null || limits.max === null) {
        return "Введите корректный размер"
      }

      if (type === "EU" || type === "RU" || type === "CM") {
        return `Размер можно ввести от ${limits.min} до ${limits.max} включительно`
      } else {
        // Для US и UK показываем строковые значения
        return `Размер можно ввести от ${limits.min} до ${limits.max} включительно`
      }
    },
    showSizeAlert(variant) {
      variant.showSizeAlert = true
      // Автоматически скрываем алерт через 3 секунды
      setTimeout(() => {
        this.hideSizeAlert(variant)
      }, 3000)
    },
    hideSizeAlert(variant) {
      variant.showSizeAlert = false
    },
    normalizeNumber(value) {
      if (value === null || value === undefined) return 0
      const normalized = String(value)
        .replace(/[\s\u00A0]/g, "")
        .replace(/,/g, ".")
        .replace(/[^\d.]/g, "")
      if (!normalized) {
        return 0
      }
      return Number(normalized)
    },
    isVariantValid(variant) {
      const sizeFilled = Boolean((variant.size || "").trim())
      const count = this.normalizeNumber(variant.count)
      const price = this.normalizeNumber(variant.price)
      return sizeFilled && variant.size_type && count > 0 && price > 0
    },
    async handleSubmit() {
      if (!this.canSubmit || this.isSubmitting) return
      this.isSubmitting = true

      // Берем первый валидный вариант (для редактирования одного продукта)
      const validVariant = this.variants.find((variant) =>
        this.isVariantValid(variant)
      )

      if (!validVariant) {
        this.isSubmitting = false
        this.updateMainButtonState()
        return
      }

      // Конвертируем размер в EU для отправки на сервер
      let euSize = validVariant.size.trim()
      const currentSizeType = validVariant.size_type || "EU"

      if (currentSizeType.toUpperCase() !== "EU") {
        const converted = convertSize(euSize, currentSizeType, "EU")
        if (converted !== null) {
          euSize = String(converted)
        } else {
          // Если конвертация не удалась, пытаемся найти ближайший размер
          const closest = findClosestSize(euSize, currentSizeType)
          if (closest !== null) {
            const euConverted = convertSize(
              String(closest),
              currentSizeType,
              "EU"
            )
            if (euConverted !== null) {
              euSize = String(euConverted)
            }
          }
        }
      }

      // Формируем FormData для отправки файлов
      const formData = new FormData()
      formData.append("price", this.normalizeNumber(validVariant.price))
      formData.append("count", this.normalizeNumber(validVariant.count))
      formData.append("has_box", Boolean(validVariant.hasBox))
      formData.append("is_custom", Boolean(validVariant.isCustom))
      formData.append("is_trade", Boolean(validVariant.allowTrade))

      // Добавляем кастомные изображения, если они есть
      // Отправляем только новые файлы (File объекты), не старые URL (строки)
      if (validVariant.isCustom) {
        if (
          validVariant.customImage1 &&
          validVariant.customImage1 instanceof File
        ) {
          formData.append("custom_images", validVariant.customImage1)
        }
        if (
          validVariant.customImage2 &&
          validVariant.customImage2 instanceof File
        ) {
          formData.append("custom_images", validVariant.customImage2)
        }
      }

      try {
        await this.$store.dispatch("updateProduct", {
          productId: this.productId,
          payload: formData,
        })
        this.notifySuccess()
        this.$router.replace({ name: "MyProducts" })
      } catch (error) {
        this.notifyError()
      } finally {
        this.isSubmitting = false
        this.updateMainButtonState()
      }
    },
    async handleDeleteProduct() {
      const tg = window.Telegram?.WebApp
      const confirmDelete = window.confirm(
        "Вы действительно хотите удалить этот продукт?"
      )
      if (!confirmDelete) return

      try {
        await this.$store.dispatch("deleteProduct", this.productId)
        if (tg?.showPopup) {
          tg.showPopup({
            title: "Успешно",
            message: "Продукт удален",
            buttons: [{ type: "close", text: "Ок" }],
          })
        } else {
          window.alert("Продукт удален")
        }
        this.$router.replace({ name: "MyProducts" })
      } catch (error) {
        window.console.error(error)
        if (tg?.showPopup) {
          tg.showPopup({
            title: "Ошибка",
            message: "Не удалось удалить товар",
            buttons: [{ type: "close", text: "Ок" }],
          })
        } else {
          window.alert("Не удалось удалить товар")
        }
      }
    },
    notifySuccess() {
      const tg = window.Telegram?.WebApp
      if (tg?.showPopup) {
        tg.showPopup({
          title: "Готово",
          message: "Изменения сохранены.",
          buttons: [{ type: "close", text: "Ок" }],
        })
      } else {
        window.alert("Изменения сохранены.")
      }
    },
    notifyError() {
      const tg = window.Telegram?.WebApp
      if (tg?.showPopup) {
        tg.showPopup({
          title: "Ошибка",
          message: "Не удалось сохранить изменения. Попробуйте позже.",
          buttons: [{ type: "close", text: "Понятно" }],
        })
      } else {
        window.alert("Не удалось сохранить изменения. Попробуйте позже.")
      }
    },
    setupMainButton() {
      const tg = window.Telegram?.WebApp
      const mb = tg?.MainButton
      if (!mb) {
        this.hasTelegramMainButton = false
        return
      }
      this.hasTelegramMainButton = true
      this.mainButtonInstance = mb
      if (this.mbHandler) {
        mb.offClick(this.mbHandler)
      }
      this.mbHandler = () => this.handleSubmit()
      mb.onClick(this.mbHandler)
      mb.textColor = "#ffffff"
      mb.color = "#e65c00"
      this.updateMainButtonState()
      mb.show()
    },
    updateMainButtonState() {
      const mb = this.mainButtonInstance || window.Telegram?.WebApp?.MainButton
      if (!mb) return
      const text = this.isSubmitting ? "Сохраняем..." : "Сохранить изменения"
      if (typeof mb.setText === "function") {
        mb.setText(text)
      } else {
        mb.text = text
      }
      const disabled = this.isSubmitting || !this.canSubmit
      const activeColor = "#e65c00"
      const disabledColor = "#d7d8dd"
      if (disabled) {
        mb.disable?.()
        mb.color = disabledColor
      } else {
        mb.enable?.()
        mb.color = activeColor
      }
      mb.textColor = "#ffffff"
      if (this.hasTelegramMainButton) {
        mb.show?.()
      }
    },
    destroyMainButton() {
      const mb = this.mainButtonInstance || window.Telegram?.WebApp?.MainButton
      if (mb && this.mbHandler) {
        mb.offClick(this.mbHandler)
      }
      if (mb) {
        mb.hide?.()
      }
      this.mbHandler = null
      this.mainButtonInstance = null
      this.hasTelegramMainButton = false
    },
    openFaq() {
      this.$router.push({ name: "Faq" })
    },
    openSizeTypeDropdown(variantId, event) {
      if (this.openDropdownId === variantId) {
        // Закрываем с анимацией
        this.closeSizeTypeDropdown(variantId)
        return
      }
      // Если другая модалка открыта, закрываем её
      if (this.openDropdownId !== null) {
        this.closeSizeTypeDropdown(this.openDropdownId)
        // Ждем завершения анимации закрытия
        setTimeout(() => {
          this.openSizeTypeDropdown(variantId, event)
        }, 200)
        return
      }
      this.dropdownClosingId = null
      this.openDropdownId = variantId
      if (event && event.target) {
        const rect = event.target.getBoundingClientRect()
        this.dropdownPosition = {
          top: rect.bottom + 4,
          left: rect.left,
        }
      }
    },
    closeSizeTypeDropdown(variantId) {
      if (this.openDropdownId === variantId) {
        this.dropdownClosingId = variantId
        setTimeout(() => {
          this.openDropdownId = null
          this.dropdownClosingId = null
        }, 200) // Время анимации закрытия
      }
    },
    selectSizeType(variantId, type) {
      const variant = this.variants.find((v) => v.id === variantId)
      if (variant) {
        const oldType = variant.size_type || "EU"
        const oldSize = variant.size

        // Если есть введенный размер, конвертируем его в новый тип
        if (oldSize && oldSize.trim() !== "") {
          // Сначала пытаемся конвертировать напрямую
          let convertedSize = convertSize(oldSize, oldType, type)

          // Если прямой конвертации нет, конвертируем через EU
          if (convertedSize === null && oldType.toUpperCase() !== "EU") {
            const euSize = convertSize(oldSize, oldType, "EU")
            if (euSize !== null) {
              convertedSize = convertSize(euSize, "EU", type)
            }
          }

          // Если конвертация удалась, обновляем размер
          if (convertedSize !== null) {
            variant.size = String(convertedSize)
            this.hideSizeAlert(variant)
          } else {
            // Если конвертация не удалась, находим ближайший валидный размер для нового типа
            const closestSize = findClosestSize(oldSize, type)
            if (closestSize !== null) {
              variant.size = String(closestSize)
              this.hideSizeAlert(variant)
            } else {
              // Если не удалось найти, очищаем поле
              variant.size = ""
            }
          }
        }

        variant.size_type = type
      }
      if (this.openDropdownId === variantId) {
        this.closeSizeTypeDropdown(variantId)
      }
    },
    handleDocumentClick(event) {
      if (!event.target.closest(`.${this.$style.sizeTypeWrapper}`)) {
        if (this.openDropdownId !== null) {
          this.closeSizeTypeDropdown(this.openDropdownId)
        }
      }
    },
    handleScroll() {
      // Закрываем дропдаун при скролле
      if (this.openDropdownId !== null) {
        this.closeSizeTypeDropdown(this.openDropdownId)
      }
    },
    triggerImageInput(variant, imageNumber) {
      const inputRef = this.$refs[`customImage${imageNumber}_${variant.id}`]
      if (inputRef && inputRef[0]) {
        inputRef[0].click()
      } else if (inputRef) {
        inputRef.click()
      }
    },
    handleCustomImageChange(variant, imageNumber, event) {
      const file = event.target.files?.[0]
      if (file) {
        if (imageNumber === 1) {
          variant.customImage1 = file
        } else {
          variant.customImage2 = file
        }
        // Сбрасываем значение инпута, чтобы можно было выбрать тот же файл повторно
        event.target.value = ""
      }
    },
    removeCustomImage(variant, imageNumber) {
      if (imageNumber === 1) {
        variant.customImage1 = null
      } else {
        variant.customImage2 = null
      }
    },
    getImagePreview(file) {
      if (!file) return ""
      if (typeof file === "string") {
        // Если это URL строки
        return file
      }
      // Если это объект с полем url, возвращаем url
      if (file && typeof file === "object" && file.url) {
        return file.url
      }
      // Если это File объект, создаем preview URL
      if (file instanceof File || file instanceof Blob) {
        return URL.createObjectURL(file)
      }
      // Если ничего не подошло, возвращаем пустую строку
      return ""
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 0.625rem 1rem 6rem 1rem;
  min-height: 100vh;
  background: #f5f6fb;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.fieldGroup {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.sectionLabel {
  padding-left: 1rem;
  font-size: 0.875rem;
  color: #6d6d72;
  font-weight: 400;
}

.galleryWrap {
  position: relative;
  margin-bottom: 1rem;
  background: #fff;
  border-radius: 0.625rem;
  padding: 1rem 0;
  img {
    transform: scale(-1, 1);
  }
}

.inputCard {
  background: #fff;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
}

.inputCardDisabled {
  background: #f5f5f5;
  opacity: 0.7;
  cursor: not-allowed;
}

.modelInput {
  width: 100%;
  border: none;
  background: none;
  font-size: 1rem;
  color: #8e8e93;
  outline: none;
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: not-allowed;
}
.modelInput[value]:not([value=""]) {
  color: #6d6d72;
}
.modelInput:disabled {
  color: #6d6d72;
  cursor: not-allowed;
}

.variantsSection {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.variantCard {
  background: #fff;
  border-radius: 0.625rem;
  padding: 0.25rem 1rem 0.25rem 1rem;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
}

.variantTopRow {
  display: grid;
  grid-template-columns: min-content min-content 1fr;
  align-items: center;
  padding: 0.625rem 0;
  gap: 0.75rem;
}

.variantInputBlock {
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  &:first-child {
    .invisibleInput {
      min-width: 1.25rem;
      width: auto;
      max-width: 4rem;
    }
  }
}

.combinedInput {
  display: flex;
  align-items: baseline;
  gap: 0.2rem;
  font-size: 1rem;
  color: #000;
  white-space: nowrap;
  position: relative;
}
.combinedInput span {
  color: #000;
  font-weight: 400;
}

.invisibleInput {
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-size: 1rem;
  color: #000;
  outline: none;
  font-weight: 400;
  max-width: 4rem;
}

.sizeInputWrapper {
  position: relative;
  display: flex;
  flex-direction: column;
}

.countInput {
  text-align: right;
}

.sizeAlert {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.25rem;
  padding: 0.375rem 0.5rem;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  color: #856404;
  white-space: nowrap;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.addVariantSize {
  text-align: right;
  justify-self: end;
}

.countCombined {
  min-width: 3.5rem;
}

.addVariantCount {
  text-align: right;
  justify-self: end;
}

.priceInput {
  width: 100%;
  max-width: 100%;
  text-align: right;
}

.priceCombined {
  justify-content: flex-end;
  width: 100%;
}

.sizeTypeWrapper {
  position: relative;
}

.sizeTypeLabel {
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.sizeTypeArrow {
  width: 0.75rem;
  height: 0.5rem;
  color: currentColor;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.sizeTypeArrowOpen {
  transform: rotate(180deg);
}

.sizeTypeDropdown {
  position: fixed;
  background: #fff;
  border: 1px solid #b8b8bc5c;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  min-width: 4rem;
  display: flex;
  flex-direction: column;
  opacity: 1;
  transform: translateY(0) scale(1);
  transform-origin: top left;
  transition: opacity 0.2s ease, transform 0.2s ease;
  animation: dropdownOpen 0.2s ease forwards;
}

.sizeTypeDropdownClosing {
  animation: dropdownClose 0.2s ease forwards;
}

@keyframes dropdownOpen {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes dropdownClose {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-8px) scale(0.95);
  }
}

.sizeTypeOption {
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 1rem;
  color: #000;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
  display: block;

  &:hover {
    background: #f5f5f5;
  }

  &:first-child {
    border-radius: 0.5rem 0.5rem 0 0;
  }

  &:last-child {
    border-radius: 0 0 0.5rem 0.5rem;
  }
}

.sizeTypeOptionActive {
  background: #f5f7fa;
  font-weight: 600;
}

.variantDivider {
  height: 1px;
  background: #f0f0f5;
  margin: 0 -1rem;
}

.variantBottomRow {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.4rem 0;
}

.variantBottomItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.variantBottomRow span {
  font-size: 1rem;
  color: #000;
}

.switchGroup {
  background: #fff;
  border-radius: 0.625rem;
  padding: 0 1rem;
  margin-top: 1rem;
}

.switchRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0;
  font-size: 1rem;
  color: #000;
}

.groupDivider {
  height: 1px;
  background: #f0f0f5;
  margin: 0;
}

.toggle {
  position: relative;
  width: 3.2rem;
  height: 1.9rem;
  cursor: pointer;
  display: inline-block;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle span {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e9e9ea;
  transition: 0.3s;
  border-radius: 2rem;
}

.toggle span:before {
  position: absolute;
  content: "";
  height: 1.7rem;
  width: 1.7rem;
  left: 0.1rem;
  bottom: 0.1rem;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle input:checked + span {
  background-color: #000;
}
.toggleBlack input:checked + span {
  background-color: #000;
}

.toggle input:checked + span:before {
  transform: translateX(1.3rem);
}

.links {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.links button {
  border: none;
  background: none;
  color: #8e8e93;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem;
}
.arrowIcon {
  font-size: 1.2rem;
  line-height: 1;
  margin-top: -2px;
}

.submitBar {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.submitBtn {
  width: 100%;
  background: #ff6600;
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
}
.submitBtn:disabled {
  opacity: 0.55;
  box-shadow: none;
}

.deleteProductBtn {
  width: 100%;
  background: transparent;
  color: #ff3b30;
  border: 1px solid #ff3b30;
  border-radius: 0.85rem;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}
.deleteProductBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.customImagesSection {
  margin-top: 1rem;
  padding: 1rem 0;
  border-top: 1px solid #f0f0f5;
}

.customImagesLabel {
  font-size: 0.875rem;
  color: #6d6d72;
  margin-bottom: 0.75rem;
  padding-left: 0;
}

.customImagesRequirements {
  font-size: 0.8125rem;
  color: #8e8e93;
  line-height: 1.4;
  margin: 0 0 0.75rem 0;
  padding: 0;
}

.customImagesGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.customImageWrapper {
  position: relative;
  aspect-ratio: 1;
  border-radius: 0.5rem;
  overflow: hidden;
}

.customImagePreview {
  position: relative;
  width: 100%;
  height: 100%;
}

.customImagePreview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.removeImageBtn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.removeImageBtn img {
  width: 1rem;
  height: 1rem;
  filter: brightness(0) invert(1);
}

.addImageBtn {
  width: 100%;
  height: 100%;
  border: 2px dashed #d1d1d6;
  border-radius: 0.5rem;
  background: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #8e8e93;
  transition: all 0.2s;
}

.addImageBtn:hover {
  border-color: #8e8e93;
  background: #f5f5f5;
}

.addImageBtn svg {
  width: 2rem;
  height: 2rem;
}

.hiddenInput {
  display: none;
}

.skeletonForm {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.skeletonFieldGroup {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.skeletonLabel {
  width: 60%;
  height: 0.875rem;
  border-radius: 0.25rem;
  margin-left: 1rem;
}

.skeletonInput {
  width: 100%;
  height: 3rem;
  border-radius: 0.625rem;
}

.skeletonGallery {
  width: 100%;
  height: 8rem;
  border-radius: 0.625rem;
}

.skeletonVariantCard {
  background: #fff;
  border-radius: 0.625rem;
  padding: 0.25rem 1rem 0.25rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.skeletonVariantTopRow {
  display: grid;
  grid-template-columns: min-content min-content 1fr;
  align-items: center;
  padding: 0.625rem 0;
  gap: 0.75rem;
}

.skeletonSize {
  width: 3rem;
  height: 1.5rem;
  border-radius: 0.25rem;
}

.skeletonCount {
  width: 3.5rem;
  height: 1.5rem;
  border-radius: 0.25rem;
}

.skeletonPrice {
  width: 6rem;
  height: 1.5rem;
  border-radius: 0.25rem;
  justify-self: flex-end;
}

.skeletonDivider {
  width: 100%;
  height: 1px;
  margin: 0 -1rem;
}

.skeletonVariantBottomRow {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.4rem 0;
}

.skeletonToggle {
  width: 100%;
  height: 1.9rem;
  border-radius: 0.25rem;
}
</style>
